import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { readProjectModule } from "../Modules/moduleNavigation";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  setActiveRoomColoringIdInRedux,
  setRotationInRedux,
  setSelectedSpaceNumberInRedux,
} from "../DesignTab/designSlice";
import { changeActiveModuleThunk } from "../DesignTab/designThunk";
import {
  IDrofusOccurrenceValidationSource,
  setDrofusOccurrenceValidationFilterModeInRedux,
  setDrofusOccurrenceValidationListTypeInRedux,
  setDrofusOccurrenceValidationResponsibilityInRedux,
  setDrofusOccurrenceValidationSourceInRedux,
} from "../DrofusOccurrenceValidation/drofusOccurrenceValidationSlice";
import { parseDrofusOccurrenceValidationColoringId } from "../DrofusOccurrenceValidation/drofusOccurrenceValidationIds";

type UrlSyncOptions = {
  module?: boolean;
  floor?: boolean;
  rotation?: boolean;
  selectedRoom?: boolean;
  roomColoring?: boolean;
  drofusOccurrenceValidation?: boolean;
};

const DEFAULT_OPTIONS: Required<UrlSyncOptions> = {
  module: true,
  floor: true,
  rotation: true,
  selectedRoom: true,
  roomColoring: true,
  drofusOccurrenceValidation: true,
};

const isDrofusOccurrenceValidationSource = (
  value: string | null
): value is IDrofusOccurrenceValidationSource => {
  return value === "imported" || value === "liveDrofus";
};


export const useAppUrlStateSync = (options?: UrlSyncOptions) => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const activeModule = useAppSelector((state) => state.design.activeModule);
  const floorName = useAppSelector((state) => state.design.floorAndJob?.floorName ?? null);
  const building = useAppSelector((state) => state.design.floorAndJob?.building?.trim() ?? null);
  const rotation = useAppSelector((state) => state.design.rotation);
  const selectedRoomNumber = useAppSelector((state) => state.design.selectedSpaceNumber);
  const activeRoomColoringId = useAppSelector((state) => state.design.activeRoomColoringId);
  const occurrenceFilterMode = useAppSelector(
    (state) => state.drofusOccurrenceValidation.filterMode
  );
  const occurrenceSource = useAppSelector(
    (state) => state.drofusOccurrenceValidation.specifiedArticlesSource
  );
  const occurrenceListType = useAppSelector(
    (state) => state.drofusOccurrenceValidation.specifiedArticlesListType
  );
  const occurrenceResponsibility = useAppSelector(
    (state) => state.drofusOccurrenceValidation.specifiedArticlesResponsibility
  );

  const hydratedRef = useRef(false);
  const lastHydratedSearchRef = useRef<string | null>(null);
  const lastWrittenSearchRef = useRef<string | null>(null);
  const skipNextWriteRef = useRef(false);

  useEffect(() => {
    if (searchParams.has("state")) return;
    const search = searchParams.toString();
    if (search === lastWrittenSearchRef.current || search === lastHydratedSearchRef.current) {
      return;
    }

    lastHydratedSearchRef.current = search;

    let hydratedFromUrl = false;
    let moduleChanged = false;

    if (config.module) {
      const moduleKey = readProjectModule(searchParams.toString());
      if (moduleKey && moduleKey !== activeModule) {
        dispatch(changeActiveModuleThunk(moduleKey));
        moduleChanged = true;
        hydratedFromUrl = true;
      }
    }

    if (config.rotation) {
      const rotParam = searchParams.get("rot");
      if (rotParam != null) {
        const parsed = Number(rotParam);
        if (Number.isFinite(parsed) && parsed !== rotation) {
          dispatch(setRotationInRedux(parsed));
          hydratedFromUrl = true;
        }
      }
    }

    if (config.selectedRoom) {
      const roomParam = searchParams.get("room") ?? searchParams.get("roomNumber");
      if (roomParam != null) {
        const nextRoomNumber = roomParam.trim() || undefined;
        if (nextRoomNumber !== selectedRoomNumber) {
          dispatch(setSelectedSpaceNumberInRedux(nextRoomNumber));
          hydratedFromUrl = true;
        }
      }
    }

    if (config.roomColoring) {
      const colorParam = searchParams.get("color");
      if (colorParam != null) {
        const next = colorParam.trim() || undefined;
        // Module activation resets the coloring. The selector still contains the
        // pre-dispatch value, which may already match the URL via localStorage.
        if (moduleChanged || next !== activeRoomColoringId) {
          dispatch(setActiveRoomColoringIdInRedux(next));
          hydratedFromUrl = true;
        }
      }
    }

    if (config.drofusOccurrenceValidation) {
      const sourceParam = searchParams.get("occurrenceSource");
      if (isDrofusOccurrenceValidationSource(sourceParam) && sourceParam !== occurrenceSource) {
        dispatch(setDrofusOccurrenceValidationSourceInRedux(sourceParam));
        hydratedFromUrl = true;
      }

      const colorParam = config.roomColoring ? searchParams.get("color") : null;
      const scope = parseDrofusOccurrenceValidationColoringId(colorParam);
      const listTypeParam = searchParams.get("occurrenceListType");
      const responsibilityParam = searchParams.get("occurrenceResponsibility");
      const filterModeParam = searchParams.get("occurrenceFilterMode");
      const hasOccurrenceState = sourceParam != null || colorParam != null ||
        listTypeParam != null || responsibilityParam != null || filterModeParam != null;

      if (hasOccurrenceState) {
        // Missing filters in a shared link must clear filters left in Redux.
        // Older links can express the selection using only the coloring ID.
        const nextListType = listTypeParam != null
          ? listTypeParam.trim() || null
          : scope?.type === "listType" ? scope.value : null;
        const nextResponsibility = responsibilityParam != null
          ? responsibilityParam.trim() || null
          : scope?.type === "responsibility" ? scope.value : null;
        const nextFilterMode = filterModeParam === "listType" || filterModeParam === "responsibility"
          ? filterModeParam
          : nextResponsibility ? "responsibility" : "listType";

        if (nextFilterMode !== occurrenceFilterMode) {
          dispatch(setDrofusOccurrenceValidationFilterModeInRedux(nextFilterMode));
          hydratedFromUrl = true;
        }
        if (nextListType !== occurrenceListType) {
          dispatch(setDrofusOccurrenceValidationListTypeInRedux(nextListType));
          hydratedFromUrl = true;
        }
        if (nextResponsibility !== occurrenceResponsibility) {
          dispatch(setDrofusOccurrenceValidationResponsibilityInRedux(nextResponsibility));
          hydratedFromUrl = true;
        }
      }
    }

    hydratedRef.current = true;
    skipNextWriteRef.current = hydratedFromUrl;
  }, [
    activeModule,
    activeRoomColoringId,
    config.drofusOccurrenceValidation,
    config.module,
    config.roomColoring,
    config.rotation,
    config.selectedRoom,
    dispatch,
    occurrenceFilterMode,
    occurrenceListType,
    occurrenceResponsibility,
    occurrenceSource,
    rotation,
    searchParams,
    selectedRoomNumber,
  ]);

  useEffect(() => {
    if (searchParams.has("state")) return;
    if (!hydratedRef.current) return;

    if (skipNextWriteRef.current) {
      skipNextWriteRef.current = false;
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("pns");

        if (config.floor && floorName) {
          next.set("floor", floorName);
          if (building) next.set("building", building);
          else next.delete("building");
        }

        if (config.rotation && rotation != null) {
          next.set("rot", String(rotation));
        }

        if (config.selectedRoom) {
          const nextRoom = selectedRoomNumber?.trim() ?? "";
          if (nextRoom) {
            next.set("room", nextRoom);
          } else {
            next.delete("room");
          }
          next.delete("roomNumber");
        }

        if (config.module) {
          if (activeModule) {
            next.set("module", String(activeModule));
          } else {
            next.delete("module");
          }
        }

        if (config.roomColoring) {
          const nextColor = activeRoomColoringId?.trim() ?? "";
          if (nextColor) {
            next.set("color", nextColor);
          } else {
            if (activeModule === "ROOM_VALIDATION") next.set("color", "");
            else next.delete("color");
          }
        }

        if (config.drofusOccurrenceValidation) {
          const shouldPersistOccurrenceState =
            activeModule === "ROOM_VALIDATION" ||
            occurrenceSource !== "imported" ||
            occurrenceFilterMode !== "listType" ||
            occurrenceListType != null ||
            occurrenceResponsibility != null;

          if (shouldPersistOccurrenceState) {
            next.set("occurrenceSource", occurrenceSource);
            next.set("occurrenceFilterMode", occurrenceFilterMode);

            const nextListType = occurrenceListType?.trim() ?? "";
            if (nextListType) {
              next.set("occurrenceListType", nextListType);
            } else {
              next.delete("occurrenceListType");
            }

            const nextResponsibility = occurrenceResponsibility?.trim() ?? "";
            if (nextResponsibility) {
              next.set("occurrenceResponsibility", nextResponsibility);
            } else {
              next.delete("occurrenceResponsibility");
            }
          } else {
            next.delete("occurrenceSource");
            next.delete("occurrenceFilterMode");
            next.delete("occurrenceListType");
            next.delete("occurrenceResponsibility");
          }
        }

        const prevSearch = prev.toString();
        const nextSearch = next.toString();
        if (prevSearch === nextSearch) return prev;

        lastWrittenSearchRef.current = nextSearch;
        return next;
      },
      { replace: true }
    );
  }, [
    activeModule,
    activeRoomColoringId,
    config.drofusOccurrenceValidation,
    config.floor,
    config.module,
    config.roomColoring,
    config.rotation,
    config.selectedRoom,
    floorName,
    building,
    occurrenceFilterMode,
    occurrenceListType,
    occurrenceResponsibility,
    occurrenceSource,
    rotation,
    selectedRoomNumber,
    setSearchParams,
    searchParams,
  ]);
};
